import { faArrowRight } from "@fortawesome/pro-regular-svg-icons";

import LinkButton from "common/components/LinkButton";
import Icon from "common/components/Icon";

import { urls } from "client/routes";
import { ItemClickSource } from "client/analytics/types";
import Markdown from "client/common/components/content/Markdown";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import FetchErrorAlert from "client/common/components/errors/FetchErrorAlert";
import HorizontalOverflow from "client/common/components/display/HorizontalOverflow";
import { useBreakpoints } from "client/common/hooks";
import Banner from "client/common/components/content/Banner";
import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { useCurrentDrawer } from "client/common/hooks/stackables";

import { useIncentiveItemsQuery, useIncentiveQuery } from "../hooks";
import PromotionItemCard from "./PromotionItemCard";

import styles from "../styles/IncentiveDrawer.module.less";

const noop = () => undefined;

export type IncentiveDrawerProps = {
  id: number;
  shouldDisplayItems?: boolean;
  onNavigate?: () => void;
};

// only display up to 8 items
const MAX_ITEMS = 8;

const IncentiveDrawer = ({
  id,
  shouldDisplayItems = true,
  onNavigate = noop,
}: IncentiveDrawerProps) => {
  const { close } = useCurrentDrawer();
  const { isPhoneAndSmaller } = useBreakpoints();
  const {
    data: incentive,
    error: incentiveError,
    isLoading: isIncentiveLoading,
  } = useIncentiveQuery(id);

  const {
    data: items,
    error: itemsError,
    isLoading: areItemsLoading,
  } = useIncentiveItemsQuery(
    id,
    { page_size: MAX_ITEMS, page: 1 },
    { enabled: shouldDisplayItems },
  );

  const renderItemsContent = () => {
    if (areItemsLoading) {
      return <LoadingContainer />;
    }

    if (!items || itemsError) {
      return <FetchErrorAlert error={incentiveError} />;
    }

    if (!items.results.length) {
      return null;
    }

    const handleNavigate = () => {
      close();
      onNavigate();
    };

    const renderedItems = (
      <>
        {items.results.map((item) => (
          <PromotionItemCard
            key={item.id}
            item={item}
            size="s"
            classNames={{
              description: styles.description,
              name: styles.name,
              content: styles.content,
              product: styles.product,
            }}
            onNavigate={handleNavigate}
            clickSource={ItemClickSource.Incentive}
          />
        ))}
        {items.results.length === MAX_ITEMS && !!id && (
          <LinkButton
            className={styles.link}
            dest={urls.products.incentive({ params: { id } })}
            onClick={handleNavigate}
          >
            <div className={styles.linkContent}>
              See all items for this promotion
              <Icon className={styles.arrow} icon={faArrowRight} fixedWidth={true} />
            </div>
          </LinkButton>
        )}
      </>
    );

    return (
      <div>
        <div className={styles.itemsLabel}>Applicable items:</div>
        {isPhoneAndSmaller ? (
          <HorizontalOverflow direction="right" classNames={{ scroller: styles.itemsScroller }}>
            {renderedItems}
          </HorizontalOverflow>
        ) : (
          <div className={styles.itemsGridContainer}>{renderedItems}</div>
        )}
      </div>
    );
  };

  if (isIncentiveLoading) {
    return <DrawerLayout.Loading />;
  }

  if (!incentive || incentiveError) {
    return <DrawerLayout.FetchError error={incentiveError} entityName="incentive" />;
  }

  return (
    <DrawerLayout>
      <DrawerLayout.Header title="Incentive offer details" onClose={close} />
      <DrawerLayout.Content>
        {/* Global autoship do not always have title or message, so let's not display the banner for this case for now */}
        {!!(incentive.promo_title && incentive.promo_message) && (
          <Banner variant="promotion">
            <Banner.Content label={incentive.promo_title} description={incentive.promo_message} />
          </Banner>
        )}
        {shouldDisplayItems && renderItemsContent()}
        <Markdown markdown={incentive.promo_details} />
      </DrawerLayout.Content>
    </DrawerLayout>
  );
};

export default IncentiveDrawer;
